import { createSelector } from '@reduxjs/toolkit'
import { UseQueryStateResult } from '@reduxjs/toolkit/dist/query/react/buildHooks.d'
import { createApi } from '@reduxjs/toolkit/query/react'
import { useRef } from 'react'

import baseQueryInterceptor from '@api/base-query-interceptor'
import { ApiResponse } from '@api/types/api-response.type'

import { getUserEmail } from '@utilities/helper'

import { Unit } from '@adUtilities/types/apartment'

export interface Level {
  level: string
  data: Array<Unit>
  height: number
}

interface BlockInterface {
  label: string
  order: number
  levels: {
    [key: string]: {
      order: number
      label: string
      units: {
        [key: string]: Unit
      }
    }
  }
}

export interface BlockCollectionInterface {
  [key: string]: BlockInterface
}

type BuildingType = {
  projectName: string
}

interface BuildingApiResponse {
  blockOrders: string[]
  lists: Level[]
}

const FULFILLED_STATUS = 'fulfilled'

export const buildingApi = createApi({
  reducerPath: 'buildingApi',
  baseQuery: baseQueryInterceptor,
  endpoints: (builder) => ({
    getBuilding: builder.query<ApiResponse<BuildingApiResponse>, BuildingType>({
      query: (params: BuildingType) => ({
        url: `/v1/${params.projectName}/building?user_name=${getUserEmail()}`,
        method: 'get',
      }),
    }),
  }),
})

const resetLevelBlockIds = (levels: Level[]) =>
  levels.map((level) => ({
    ...level,
    data: level?.data?.map((levelData) => ({
      ...levelData,
      blockId: null,
    })),
  }))

const selectBuildingData = createSelector(
  (data: ApiResponse<BuildingApiResponse> | undefined) =>
    data?.data?.blockOrders,
  (data: ApiResponse<BuildingApiResponse> | undefined) => data?.data?.lists,
  (blockOrders = [], levels = []) => {
    const sortedLevels = [...levels].sort(
      (itemA: Level, itemB: Level) =>
        (itemA?.height || 0) - (itemB?.height || 0)
    )

    const isSingleBlock = blockOrders.length === 1

    return {
      building: isSingleBlock ? resetLevelBlockIds(sortedLevels) : sortedLevels,
      blockOrders: isSingleBlock ? [] : blockOrders,
    }
  }
)

export const selectFromResult = ({
  data,
  isLoading,
  isError,
  status,
  error,
  isFetching,
  fulfilledTimeStamp,
}: UseQueryStateResult<any, any>): {
  building: Level[]
  blockOrders: string[]
  isLoaded: boolean
  isError: boolean
  errorStatus: number
  status: string
  isFetching: boolean
  fulfilledTimeStamp: number
} => {
  const { building, blockOrders } = selectBuildingData(data)

  return {
    building,
    blockOrders,
    isLoaded: !isLoading,
    isError,
    status,
    errorStatus: error?.status,
    isFetching,
    fulfilledTimeStamp,
  }
}

export const { useGetBuildingQuery } = buildingApi

export const useGetBuildingQuerySilent = (
  params: BuildingType,
  options?: { skip?: boolean }
) => {
  const result = useGetBuildingQuery(params, { selectFromResult, ...options })
  const hasFetchedRef = useRef(false)

  if (result.status === FULFILLED_STATUS) {
    hasFetchedRef.current = true
  }

  return {
    ...result,
    isFetching: hasFetchedRef.current ? false : result.isFetching,
  }
}
